import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { 
  Rocket, Check, PartyPopper, ArrowRight, Loader2, 
  Eye, TrendingUp, Crown, Star, Sparkles
} from 'lucide-react';
import confetti from 'canvas-confetti';

const BoostSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [boostActivated, setBoostActivated] = useState(false);
  const [listingId, setListingId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  const sessionId = searchParams.get('session_id');
  const listingIdParam = searchParams.get('listing_id');

  useEffect(() => {
    if (sessionId) {
      checkBoostStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    // Trigger confetti on success
    if (boostActivated) {
      const duration = 3 * 1000;
      const end = Date.now() + duration;

      const colors = ['#FFD700', '#FFA500', '#FF6347', '#2E7D32'];

      (function frame() {
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  }, [boostActivated]);

  const checkBoostStatus = async () => {
    try {
      const response = await api.get(`/boost/status/${sessionId}`);
      
      if (response.data.status === 'success' && response.data.boost_activated) {
        setBoostActivated(true);
        setListingId(response.data.listing_id || listingIdParam);
        setExpiresAt(response.data.expires_at);
      } else if (response.data.status === 'pending') {
        // Retry after a short delay
        setTimeout(checkBoostStatus, 2000);
        return;
      }
    } catch (error) {
      console.error('Error checking boost status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">{language === 'nl' ? 'Boost wordt geactiveerd...' : 'Activating boost...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137] flex items-center justify-center p-4" data-testid="boost-success-page">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-[10%] w-96 h-96 bg-amber-500/30 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-20 right-[10%] w-[500px] h-[500px] bg-[#2E7D32]/30 rounded-full blur-[150px] animate-pulse" style={{animationDelay: '1s'}} />
      </div>

      <div className="relative z-10 max-w-lg w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 p-8 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-4 left-4 w-8 h-8 border-2 border-white/30 rounded-full" />
              <div className="absolute top-10 right-10 w-4 h-4 bg-white/20 rounded-full" />
              <div className="absolute bottom-8 left-8 w-6 h-6 bg-white/20 rounded" />
            </div>
            
            <div className="relative">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Rocket className="w-10 h-10 text-amber-500" />
              </div>
              
              <h1 className="text-3xl font-black mb-2 flex items-center justify-center gap-2">
                <PartyPopper className="w-8 h-8" />
                {language === 'nl' ? 'Boost Geactiveerd!' : 'Boost Activated!'}
                <PartyPopper className="w-8 h-8" />
              </h1>
              
              <p className="text-white/90">
                {language === 'nl' 
                  ? 'Jouw woning staat nu bovenaan!'
                  : 'Your listing is now at the top!'}
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="p-6">
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2A2A2A]">
                    {language === 'nl' ? 'Bovenaan in resultaten' : 'Top of results'}
                  </h3>
                  <p className="text-sm text-[#9E9E9E]">
                    {language === 'nl' ? 'Huurders zien jouw woning als eerste' : 'Tenants see your property first'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2A2A2A]">
                    {language === 'nl' ? '5x meer views' : '5x more views'}
                  </h3>
                  <p className="text-sm text-[#9E9E9E]">
                    {language === 'nl' ? 'Gemiddeld bij gebooste advertenties' : 'Average for boosted listings'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Crown className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2A2A2A]">
                    {language === 'nl' ? 'Premium badge' : 'Premium badge'}
                  </h3>
                  <p className="text-sm text-[#9E9E9E]">
                    {language === 'nl' ? 'Extra zichtbaarheid met gouden label' : 'Extra visibility with gold label'}
                  </p>
                </div>
              </div>
            </div>

            {/* Expiry info */}
            {expiresAt && (
              <div className="text-center mb-6 p-4 bg-[#F7F7F7] rounded-xl">
                <p className="text-sm text-[#9E9E9E]">
                  {language === 'nl' ? 'Boost actief tot' : 'Boost active until'}
                </p>
                <p className="font-bold text-[#2A2A2A]">
                  {new Date(expiresAt).toLocaleDateString('nl-NL', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
            )}

            {/* CTA */}
            <Button
              onClick={() => navigate('/verhuurder/dashboard')}
              className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg"
            >
              {language === 'nl' ? 'Naar mijn dashboard' : 'Go to my dashboard'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Social proof */}
            <div className="mt-6 text-center">
              <div className="flex justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-xs text-[#9E9E9E]">
                {language === 'nl' 
                  ? '98% van verhuurders met boost vindt sneller een huurder'
                  : '98% of landlords with boost find tenants faster'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoostSuccessPage;
